
import { TimeEntry } from "@/types/time-entry";
import { formatInTimeZone } from "date-fns-tz";
import { de } from "date-fns/locale";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SalaryTimeEntry } from "./pdf/types";
import { PDF_CONFIGS, TABLE_COLUMN_WIDTHS, RGB } from "./pdf/constants";
import { formatMonth } from "./pdf/formatters";
import { calculateBreakTime, calculateEffectiveTotalAmount, calculateDayTotalMinutes } from "./pdf/calculators";
import { generateTimeTableData } from "./pdf/tableDataGenerator";
import { COMPANY_INFO } from "@/config/companyInfo";

// Function to determine Lohnabrechnung number based on month
const getLohnAbrechnungNumber = (date: Date): string => {
  // Standardize to first day of month for comparison
  const monthDate = new Date(date.getFullYear(), date.getMonth(), 1);
  
  // February and March 2024 are fixed at 001
  const feb2024 = new Date(2024, 1, 1); // February 2024
  const mar2024 = new Date(2024, 2, 1); // March 2024
  const apr2024 = new Date(2024, 3, 1); // April 2024
  
  if (monthDate <= mar2024) {
    return "001";
  }
  
  // Calculate months since April 2024
  const monthsSinceApr2024 = 
    (monthDate.getFullYear() - apr2024.getFullYear()) * 12 + 
    (monthDate.getMonth() - apr2024.getMonth());
  
  // Start from 002 for April 2024
  const lohnAbrechnungNumber = monthsSinceApr2024 + 2;
  
  // Format with leading zeros to ensure 3 digits
  return lohnAbrechnungNumber.toString().padStart(3, '0');
};

export interface SalaryPDFEmployeeInfo {
  name?: string;
  address?: string;
  city?: string;
  ahv_number?: string;
  employee_number?: string;
}

export const generateSalaryPDF = async (
  timeEntries: (TimeEntry & { user_full_name: string; hourly_rate: number })[],
  employeeInfo?: SalaryPDFEmployeeInfo
) => {
  if (!timeEntries?.length) return;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  const entry = timeEntries[0];
  const currentDate = formatInTimeZone(new Date(), "Europe/Zurich", "dd.MM.yy", { locale: de });
  const month = formatMonth(new Date(entry.start_time));
  const entryDate = new Date(entry.start_time);
  const lohnAbrechnungNumber = getLohnAbrechnungNumber(entryDate);
  const hourlyRate = entry.hourly_rate || 50; // Fallback to 50 if not set

  // Employee name from explicit info, or fall back to time entry user name
  const empName = employeeInfo?.name || entry.user_full_name;
  const empAddress = employeeInfo?.address || '';
  const empCity = employeeInfo?.city || '';
  const empAhv = employeeInfo?.ahv_number || '';
  const empNumber = employeeInfo?.employee_number || '';

  // Company info from centralized SSOT
  const companyLines = COMPANY_INFO.name.split('\n').concat(COMPANY_INFO.address.split('\n'));

  pdf.setFontSize(11);
  pdf.text("Arbeitgeber:", 15, 23);
  pdf.text("Arbeitnehmerin:", 120, 23);

  let yComp = 30;
  for (const line of companyLines) {
    pdf.text(line, 15, yComp);
    yComp += 7;
  }

  pdf.text(empName, 120, 30);
  if (empAddress) pdf.text(empAddress, 120, 37);
  if (empCity) pdf.text(empCity, 120, 44);

  pdf.setFontSize(14);
  pdf.text(`Lohnabrechnung ${month}`, 15, 70);

  pdf.setFontSize(10);
  pdf.text("Seite:", 15, 85);
  pdf.text("1/1", 50, 85);
  pdf.text("Datum:", 15, 92);
  pdf.text(currentDate, 50, 92);
  pdf.text("Lohnabrechnung:", 15, 99);
  pdf.text(lohnAbrechnungNumber, 50, 99);
  if (empNumber) {
    pdf.text("Mitarbeiter-Nr.:", 15, 106);
    pdf.text(empNumber, 50, 106);
  }

  if (empAhv) {
    pdf.text("AHV-Nr.:", 120, 85);
    pdf.text(empAhv, 155, 85);
  }

  const entriesByDate = timeEntries.reduce((acc, entry) => {
    const date = formatInTimeZone(new Date(entry.start_time), "Europe/Zurich", "yyyy-MM-dd");
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {} as Record<string, SalaryTimeEntry[]>);

  const timeTableData = generateTimeTableData(entriesByDate, hourlyRate);

  autoTable(pdf, {
    startY: 150,
    head: [["Datum", "Von", "Bis", "Pause", "Std", "Betrag"]],
    body: timeTableData,
    styles: { fontSize: 8 },
    headStyles: { fillColor: PDF_CONFIGS.HEADER_BG_COLOR, textColor: [0, 0, 0] as RGB },
    columnStyles: {
      0: { cellWidth: TABLE_COLUMN_WIDTHS.DATE },
      1: { cellWidth: TABLE_COLUMN_WIDTHS.START_TIME },
      2: { cellWidth: TABLE_COLUMN_WIDTHS.END_TIME },
      3: { cellWidth: TABLE_COLUMN_WIDTHS.BREAK },
      4: { cellWidth: TABLE_COLUMN_WIDTHS.HOURS },
      5: { cellWidth: TABLE_COLUMN_WIDTHS.AMOUNT }
    }
  });

  let totalMinutes = 0;
  const totalBreakMinutes = Object.values(entriesByDate)
    .reduce((acc, dayEntries) => {
      const dayTotalMinutes = calculateDayTotalMinutes(dayEntries);
      totalMinutes += dayTotalMinutes;
      const dayTotalHours = dayTotalMinutes / 60;
      return acc + calculateBreakTime(dayTotalHours);
    }, 0);

  const { effectiveTotalHours, grossAmount, ahvIvEo, netAmount } = calculateEffectiveTotalAmount(
    totalMinutes,
    totalBreakMinutes,
    hourlyRate
  );

  // Calculate vacation allowance (Ferienanteil)
  const vacationPercentage = PDF_CONFIGS.VACATION_PERCENTAGE;
  const vacationAmount = +(grossAmount * vacationPercentage / 100).toFixed(2);

  const salaryData = [
    ["Bruttolohn", `${effectiveTotalHours.toFixed(2)} Std`, hourlyRate.toFixed(2), grossAmount.toFixed(2)],
    ["darin enthaltener Ferienanteil", grossAmount.toFixed(2), `${vacationPercentage}%`, vacationAmount.toFixed(2)],
    ["AHV/IV/EO", grossAmount.toFixed(2), "5.15%", (-ahvIvEo).toFixed(2)],
    ["Nettolohn", "", "", netAmount.toFixed(2)]
  ];

  const finalY = (pdf as any).lastAutoTable.finalY || 200;

  autoTable(pdf, {
    startY: finalY + 20,
    head: [["Lohnart", "Basis", "Ansatz", "Betrag"]],
    body: salaryData,
    styles: { fontSize: 8 },
    headStyles: { fillColor: PDF_CONFIGS.HEADER_BG_COLOR, textColor: [0, 0, 0] as RGB },
    bodyStyles: { fontStyle: 'normal' },
    didParseCell: (data) => {
      if (data.row.index === 3) {
        data.cell.styles.fontStyle = 'bold';
      }
      // Add italics to the vacation row
      if (data.row.index === 1) {
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.textColor = [100, 100, 100];
      }
    }
  });

  // Add remarks section after the salary table
  const remarksY = (pdf as any).lastAutoTable.finalY + 10;
  pdf.setFontSize(8);
  pdf.text([
    "Bemerkungen:",
    "- Ferien- und Feiertagsentschädigung sind im Stundenlohn inbegriffen",
    `- Der inkludierte Ferienanteil beträgt ${vacationPercentage}%`,
    "- Bei einer Arbeitszeit von mehr als 5.5 Stunden pro Tag wird eine bezahlte Pause von 15 Minuten gewährt",
    "- Diese Lohnabrechnung wurde elektronisch erstellt und ist ohne Unterschrift gültig",
    "- Bitte bewahren Sie diese Abrechnung sorgfältig auf",
  ], 15, remarksY);

  // Add yellow line at the bottom of the page
  const yellowLineColor = PDF_CONFIGS.YELLOW_LINE_COLOR;
  pdf.setFillColor(yellowLineColor[0], yellowLineColor[1], yellowLineColor[2]);
  pdf.rect(0, 292, 210, 5, 'F');

  const fileName = `Lohnabrechnung-${entry.user_full_name.replace(/\s+/g, "-")}-${month.replace(/\s+/g, "-")}.pdf`;
  pdf.save(fileName);
};
